import { useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { PurchaseOrderDto } from '../types/purchaseOrder.types';
import { DeliveryTypeEnum } from '@features/receiving/types/receiving.types';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { PoStatusBadge } from './PoStatusBadge';

export type PoTableMeta = { onRefresh: () => void };

const columnHelper = createColumnHelper<PurchaseOrderDto>();

function SortableHeader({
  label,
  column,
}: {
  label: string;
  column: {
    getIsSorted: () => false | 'asc' | 'desc';
    getToggleSortingHandler: () => ((event: unknown) => void) | undefined;
  };
}) {
  const sorted = column.getIsSorted();
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 data-[state=open]:bg-accent"
      onClick={column.getToggleSortingHandler()}
    >
      {label}
      {sorted === 'asc' ? (
        <ArrowUp className="ml-1 h-3.5 w-3.5" />
      ) : sorted === 'desc' ? (
        <ArrowDown className="ml-1 h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown className="ml-1 h-3.5 w-3.5 text-muted-foreground/50" />
      )}
    </Button>
  );
}

function ResyncCell({
  id,
  poNumber,
  onRefresh,
}: {
  id: number;
  poNumber: string;
  onRefresh?: () => void;
}) {
  const [isResyncing, setIsResyncing] = useState(false);

  const handleResync = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResyncing(true);
    try {
      const result = await purchaseOrderService.resync(id);
      if (result.errors.length > 0) {
        toast.warning(result.errors[0]);
      } else {
        toast.success(`${poNumber} re-synced from NetSuite`);
      }
      onRefresh?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to resync PO');
    } finally {
      setIsResyncing(false);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResync}
            disabled={isResyncing}
          >
            {isResyncing
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <RefreshCw className="h-4 w-4" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Resync from NetSuite</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export const columns = [
  columnHelper.accessor('poNumber', {
    header: ({ column }) => <SortableHeader label="PO Number" column={column} />,
    cell: (info) => {
      const poNumber = info.getValue();
      const forAdjustment = info.row.original.forAdjustment;
      return (
        <div className="flex items-center gap-2">
          <span className="font-medium">{poNumber}</span>
          {forAdjustment && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <AlertTriangle className="h-4 w-4 text-warning" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Flagged for adjustment</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      );
    },
  }),
  columnHelper.accessor('vendorName', {
    header: ({ column }) => <SortableHeader label="Vendor" column={column} />,
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('deliveryType', {
    header: 'Type',
    enableSorting: false,
    cell: (info) => {
      const LABELS: Record<number, string> = {
        [DeliveryTypeEnum.Local]: 'Local',
        [DeliveryTypeEnum.ImportRawMaterials]: 'Import – Raw Mats',
        [DeliveryTypeEnum.ImportSpareParts]: 'Import – Spare Parts',
        [DeliveryTypeEnum.Projects]: 'Projects',
      };
      return <span className="text-xs">{LABELS[info.getValue()] ?? 'Local'}</span>;
    },
  }),
  columnHelper.accessor('poDate', {
    header: ({ column }) => <SortableHeader label="PO Date" column={column} />,
    cell: (info) => {
      const value = info.getValue();
      return value ? new Date(value).toLocaleDateString() : '-';
    },
  }),
  columnHelper.display({
    id: 'status',
    header: 'Status',
    enableSorting: false,
    cell: (info) => <PoStatusBadge po={info.row.original} />,
  }),
  columnHelper.accessor((row) => row.lines.length, {
    id: 'lineCount',
    header: 'Lines',
    enableSorting: false,
    cell: (info) => info.getValue(),
  }),
  columnHelper.display({
    id: 'received',
    header: 'Received',
    cell: (info) => {
      const lines = info.row.original.lines;
      const total = lines.reduce((sum, l) => sum + l.poQuantity, 0);
      const received = lines.reduce((sum, l) => sum + l.quantityReceived, 0);
      const pct = total > 0
        ? (received >= total ? 100 : Math.floor((received / total) * 100))
        : 0;
      return (
        <div className="flex items-center gap-2 min-w-[80px]">
          <div className="flex-1 h-2 bg-border rounded-full overflow-hidden">
            <div className="h-full bg-success" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs text-muted-foreground w-8 text-right">{pct}%</span>
        </div>
      );
    },
  }),
  columnHelper.accessor('syncedAt', {
    header: 'Synced',
    enableSorting: false,
    cell: (info) => (
      <span className="text-muted-foreground text-xs">
        {new Date(info.getValue()).toLocaleDateString()}
      </span>
    ),
  }),
  columnHelper.display({
    id: 'actions',
    header: '',
    cell: (info) => (
      <ResyncCell
        id={info.row.original.id}
        poNumber={info.row.original.poNumber}
        onRefresh={(info.table.options.meta as PoTableMeta)?.onRefresh}
      />
    ),
  }),
];
